import { Component, h } from 'preact'
import GroupDescription from './GroupDescription'
import Axios from 'axios'
import GroupActionButtons from './GroupActionButtons'
import PrivateGroupPlaceholder from './PrivateGroupPlaceholder'
import GroupMembers from './GroupMembers'
import TopicListItemView, { TopicInfo } from '../topic-list-view/TopicListItemView'
import TopicListItem from '../topic-list-view/TopicListItem'
import { common } from '../common'
import { UserData } from '../event/EventAttendees'
import FeaturedTopicsWidget, {
  FeaturedEvent,
  FeaturedTopic
} from '../widget-featured-topics/index.sync'
import { FeaturedProductUpdate } from '../../services/unified'
import { ProductAreaType } from '../../control-components/product-areas/ProductAreaSelect'
import Message from '../design-system-components/message'
import Phrase from '../../libs/translationsUtils/phrase'
import GroupJoin from './GroupJoin'
import Pagination from '../../control-components/ui-components/Pagination'
import { generateNewUrl } from '../utils/topicUrlGenerator'

export interface GroupData {
  id: number
  title: string
  description: string
  groupType: number
  curatedContent: {
    curatedTopics: Array<{id: number, type: string}>
    title: string
  }
}

interface Props {
  canSee: boolean
  group: GroupData
  csrfToken: string
  topics: TopicInfo[]
  attachmentCdn: string
  isGuest: boolean
  members: UserData[]
  isCurrentUserMember: boolean
  membershipStatus: string
  inviteStatus: 'ok' | 'fail:guest-user' | 'fail:user-already-member' | 'fail:invalid-invite' | 'fail:invalid-user-role' | undefined
  inviteToken: string
  allProductAreas: ProductAreaType[]
  featuredTopics: Array<FeaturedTopic|FeaturedEvent|FeaturedProductUpdate>
  totalPagesCount: number
  currentPage: number
}

interface State {
  userJoined: boolean
  userSubscribed: boolean
  isLoading: boolean
  requestSent: boolean
  invite: null | {
    type: 'error' | 'success'
    message: string
  }
}

export class Group extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      userJoined: props.isCurrentUserMember,
      isLoading: false,
      requestSent: props.membershipStatus === 'requested',
      userSubscribed: true,
      invite: this.getInviteState(props.inviteStatus)
    }

    this.joinGroup = this.joinGroup.bind(this)
    this.leaveGroup = this.leaveGroup.bind(this)
    this.requestJoin = this.requestJoin.bind(this)
    this.subscribe = this.subscribe.bind(this)
    this.unsubscribe = this.unsubscribe.bind(this)
  }

  getInviteState (inviteStatus: Props['inviteStatus']): State['invite'] {
    switch (inviteStatus) {
      case 'ok':
        return {
          type: 'success',
          message: 'groups.invite.joined'
        }
      case 'fail:invalid-invite':
        return {
          type: 'error',
          message: 'groups.invite.invalid_link'
        }

      case 'fail:invalid-user-role':
        return {
          type: 'error',
          message: 'groups.invite.invalid_role'
        }
      default:
        return null
    }
  }

  async componentDidMount () {
    if (!this.props.isCurrentUserMember) return

    try {
      await Axios.get(`/subscribers/subforum/${this.props.group?.id}`)
      this.setState({ userSubscribed: true })
    } catch (e) {
      this.setState({ userSubscribed: false })
    }
  }

  async joinGroup (onSucess?: Function) {
    if (!window.inSidedData.user.userid) {
      const event = new window.CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    this.setState({ isLoading: true })

    try {
      await Axios.post(`/group/${this.props.group?.id}/join`, { csrfToken: this.props.csrfToken })
      this.setState({ userJoined: true, isLoading: false, userSubscribed: true })
      if (onSucess) {
        onSucess()
      }
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async leaveGroup () {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`/group/${this.props.group?.id}/leave`, { csrfToken: this.props.csrfToken })
      this.setState({ userJoined: false, isLoading: false })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async subscribe () {
    this.setState({ isLoading: true })

    try {
      await Axios.post(`/subscribers/subforum/${this.props.group?.id}`, { csrfToken: this.props.csrfToken })
      this.setState({ isLoading: false, userSubscribed: true })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async unsubscribe () {
    this.setState({ isLoading: true })

    try {
      await Axios.delete(`/subscribers/subforum/${this.props.group?.id}`, { data: { csrfToken: this.props.csrfToken } })
      this.setState({ isLoading: false, userSubscribed: false })
    } catch (e) {
      this.setState({ isLoading: false })
    }
  }

  async requestJoin () {
    this.joinGroup(() => {
      this.setState({ requestSent: true })
    })
  }

  render ({ canSee, group, topics, isGuest, attachmentCdn, members, featuredTopics, allProductAreas = [], totalPagesCount, currentPage }: Props,
    { userJoined, userSubscribed, isLoading, requestSent, invite }: State) {
    const description = group?.description
    const isNewTopicListViewEnabled = common.isModuleActive('topicItemView')

    return (
      <div className='group-detail-wrapper'>
        {
          invite !== null
            ? (
              <Message
                variant={invite.type}
                onDismiss={() => this.setState({ invite: null })}
                timer={invite.type !== 'error' ? 5000 : undefined}
              >
                <Phrase p={invite.message} count={{ g: group?.title || '' }} />
              </Message>
              )
            : null
        }
        {
          this.props.inviteStatus === 'fail:guest-user'
            ? <GroupJoin groupTitle={this.props.group.title} groupId={this.props.group.id} token={this.props.inviteToken} />
            : null
        }
        {canSee &&
          <div className='group-featured-topics-container'>
            <GroupActionButtons
              joinGroup={this.joinGroup}
              leaveGroup={this.leaveGroup}
              subscribe={this.subscribe}
              unsubscribe={this.unsubscribe}
              isLoading={isLoading}
              isUserMember={userJoined}
              isUserSubscribed={userSubscribed}
              groupId={group.id}
            />
            <FeaturedTopicsWidget
              featuredTopics={featuredTopics}
              title={group.curatedContent.title}
              topicsFullWidth={0}
              topicIds={group.curatedContent.curatedTopics}
              allProductAreas={allProductAreas}
            />
          </div>}
        <div className='sitewidth group-detail'>
          <div className='col col--main has--side qa-div-main'>
            <div className={`${isNewTopicListViewEnabled ? 'list-widget-wrapper' : 'box'}`}>
              {canSee
                ? topics.map(topic => {
                  return isNewTopicListViewEnabled
                    ? (
                      <TopicListItemView
                        topic={topic}
                        isGuest={isGuest}
                        attachmentCdn={attachmentCdn}
                        hideCategoryMetadata
                      />
                      )
                    : (
                      <TopicListItem
                        tab={0}
                        topic={topic}
                        isGuest={isGuest}
                        attachmentCdn={attachmentCdn}
                        hideCategoryMetadata
                      />
                      )
                })
                : <PrivateGroupPlaceholder
                    onRequest={this.requestJoin}
                    isLoading={isLoading}
                    requestPending={requestSent}
                  />}
              {
                totalPagesCount > 1
                  ? (
                    <Pagination totalPageCount={totalPagesCount} currentPage={currentPage} generateNewUrl={generateNewUrl} />
                    )
                  : null
              }
            </div>
          </div>
          <div className='col col--side Sidebarmodule qa-div-sidebar'>
            <GroupDescription description={description} />
            {canSee && <GroupMembers members={members} />}
          </div>
        </div>
      </div>
    )
  }
}

export default Group
